import { ShieldX, UserX, LogOut, Calendar, User as UserIcon, FileText } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { SUSPENSION_MOTIVES } from "@/lib/userAccessMotives";

// Pantalla de bloqueo de acceso que se muestra al iniciar sesión cuando el
// super administrador suspendió o eliminó (eliminación temporal) la cuenta.
// Reemplaza toda la app: el usuario no puede usar la plataforma hasta que el
// super admin reactive el acceso. Muestra el motivo, fecha y administrador
// que el super admin registró al suspender o eliminar la cuenta.

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("es-MX"); } catch { return iso || "—"; }
}

export default function AccessBlockedScreen() {
  const { user, logout } = useAuth();
  const status = user?.status;
  const suspended = status === "suspendido";
  const eliminated = status === "eliminado";
  const title = suspended ? "Cuenta suspendida" : "Registro requerido";
  const Icon = suspended ? ShieldX : UserX;
  const message = suspended
    ? "Tu cuenta ha sido suspendida temporalmente. Para reactivar tu acceso, comunícate con el área de ventas, tu ejecutivo de cuentas, el departamento de administración o el área comercial."
    : "Para reingresar debes registrarte nuevamente y solicitar aprobación al área de ventas, tu ejecutivo de cuentas, el departamento de administración o el área comercial. Solo el super administrador puede reactivar el acceso.";

  const reason = user?.status_reason || "";
  const matched = SUSPENSION_MOTIVES.filter(m => reason.toLowerCase().includes(m.label.toLowerCase()));
  const extraIdx = reason.toLowerCase().indexOf("detalle:");
  const extra = extraIdx >= 0 ? reason.slice(extraIdx + 8).trim() : "";
  const otherLabels = reason.split(/[.;]/).map(s => s.trim()).filter(Boolean)
    .filter(s => !SUSPENSION_MOTIVES.some(m => m.label.toLowerCase() === s.toLowerCase()) && !s.toLowerCase().startsWith("detalle"));

  return (
    <div className="fixed inset-0 z-[100] bg-ink flex items-center justify-center p-6">
      <div className="surface w-full max-w-md p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border border-destructive/40 bg-destructive/10">
          <Icon className="w-7 h-7 text-destructive" />
        </div>
        <h1 className="font-display font-bold uppercase text-xl mb-3">{title}</h1>
        <p className="text-sm text-ink-muted mb-4 leading-relaxed">{message}</p>

        {(reason || user?.status_changed_at || user?.status_changed_by) && (
          <div className="surface-inset p-3 mb-5 text-left border-l-2 border-destructive/60">
            <div className="flex items-center gap-1.5 mb-2 text-destructive">
              <FileText className="w-3.5 h-3.5" />
              <span className="label">Motivo registrado por administración</span>
            </div>
            <div className="space-y-2 text-[0.78rem]">
              {matched.length > 0 && (
                <ul className="list-disc pl-4 space-y-0.5">
                  {matched.map(m => <li key={m.key}><span className="text-ink-text font-medium">{m.label}</span> — <span className="text-ink-muted">{m.desc}</span></li>)}
                </ul>
              )}
              {otherLabels.length > 0 && <p className="text-ink-text">{otherLabels.join("; ")}</p>}
              {extra && <p className="text-ink-text"><span className="text-ink-muted">Detalle: </span>{extra}</p>}
              {!reason && <p className="text-ink-muted">Sin motivo registrado.</p>}
              <div className="pt-2 border-t border-ink-border space-y-1 text-[0.72rem] text-ink-muted">
                {user?.status_changed_at && (
                  <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(user.status_changed_at)}</div>
                )}
                {user?.status_changed_by && (
                  <div className="flex items-center gap-1.5"><UserIcon className="w-3 h-3" /> Aplicado por {user.status_changed_by}</div>
                )}
              </div>
            </div>
          </div>
        )}

        <button onClick={() => logout(true)}
          className="w-full flex items-center justify-center gap-2 bg-cyan hover:bg-cyan-hover text-ink px-4 py-3 font-display font-bold uppercase text-sm">
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}